import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";

// ── Status squares ─────────────────────────────────────────────────────────────

const STATUS_COLORS = {
    green: "#4caf50",
    yellow: "#ffc107",
    red: "#f44336",
};

/**
 * Three coloured squares: Good (green) / Fair (yellow) / Poor (red).
 * The active square is filled; the others are faint outlines.
 */
function StatusSquares({ status }) {
    const squares = [
        { key: "green", color: STATUS_COLORS.green },
        { key: "yellow", color: STATUS_COLORS.yellow },
        { key: "red", color: STATUS_COLORS.red },
    ];
    return (
        <span style={{ display: "inline-flex", gap: 2, verticalAlign: "middle", flexShrink: 0, paddingRight: "20px" }}>
            {squares.map(({ key, color }) => {
                const active = status === key;
                return (
                    <span
                        key={key}
                        style={{
                            display: "inline-block",
                            width: 12,
                            height: 12,
                            backgroundColor: active ? color : "transparent",
                            border: `2px solid ${color}`,
                            borderRadius: 2,
                            opacity: active ? 1 : 0.7,
                        }}
                    />
                );
            })}
        </span>
    );
}

// ── Printable sheet ────────────────────────────────────────────────────────────

/**
 * The actual printed document.
 * Wrapped with forwardRef so useReactToPrint can attach its contentRef.
 *
 * Props:
 *   inspection  – Inspection object from DviEditor state
 *   items       – InspectionItem[]
 *   categories  – CategoryDto[]
 *   dealerName  – optional string (shown top-right if no logo)
 *   logoUrl     – optional image URL for dealer logo
 */
export const PrintDVISheet = React.forwardRef(function PrintDVISheet(
    { inspection, items = [], categories = [], dealerName, logoUrl },
    ref,
) {
    // console.log(dealerName)
    // console.log(logoUrl)
    const vehicle = inspection?.vehicle;
    const records = inspection?.records ?? {};

    // Build ordered category -> items map
    const catMap = new Map();
    for (const cat of categories) {
        const catItems = items.filter(i => i.categoryId === cat.id);
        if (catItems.length > 0) catMap.set(cat, catItems);
    }
    const allEntries = [...catMap.entries()];

    // Build a flat list of renderable rows in order: header then items per category.
    // Categories that are too long for one column will naturally split across columns.
    const flatRows = [];
    for (const [cat, catItems] of allEntries) {
        flatRows.push({ type: "header", cat });
        for (const item of catItems) {
            flatRows.push({ type: "item", cat, item });
        }
    }

    // Slice into 3 equal columns preserving order
    const colSize = Math.ceil(flatRows.length / 3);
    const columns = [
        flatRows.slice(0, colSize),
        flatRows.slice(colSize, colSize * 2),
        flatRows.slice(colSize * 2),
    ];

    const today = new Date().toLocaleDateString("en-US", {
        month: "2-digit", day: "2-digit", year: "numeric",
    });

    return (
        <div ref={ref} style={s.page}>

            {/* Header */}
            <div style={s.header}>
                <div style={s.headerSpacer}>
                    {logoUrl ? (
                        <img src={logoUrl} alt="Dealer" style={s.dealerLogo} />
                    ) : dealerName ? (
                        <div style={s.dealerNameBox}>{dealerName}</div>
                    ) : null}
                </div>

                {/* Center - qcar logo */}
                <div style={s.headerCenter}>
                    <div style={s.qcarBrand}>
                        <img
                            src="logo-dark.png"
                            alt="qcar logo"
                            style={{ width: `1.25in`, maxWidth: "100%", height: "auto" }}
                        />
                    </div>
                    <div style={s.sheetTitle}>Vehicle Inspection Checklist</div>
                </div>
                {/* Right - dealer logo */}
                <div style={s.headerRight}>
                    {logoUrl ? (
                        <img src={logoUrl} alt="Dealer" style={s.dealerLogo} />
                    ) : dealerName ? (
                        <div style={s.dealerNameBox}>{dealerName}</div>
                    ) : null}
                </div>
            </div>

            {/* Vehicle meta */}
            <div style={s.metaGrid}>
                <div style={s.metaField}>
                    <span style={s.metaLabel}>Year / Make / Model / Trim:</span>
                    <span style={s.metaUnderline}>
                        {[vehicle?.year, vehicle?.make, vehicle?.model, vehicle?.trim]
                            .filter(Boolean).join(" ") || ""}
                    </span>
                </div>
                <div style={s.metaField}>
                    <span style={s.metaLabel}>Stock #:</span>
                    <span style={s.metaUnderline}>{vehicle?.stockNumber ?? ""}</span>
                </div>
                <div style={s.metaField}>
                    <span style={s.metaLabel}>VIN:</span>
                    <span style={s.metaUnderline}>{vehicle?.vin ?? ""}</span>
                </div>
                <div style={s.metaField}>
                    <span style={s.metaLabel}>Date:</span>
                    <span style={s.metaUnderline}>{today}</span>
                </div>
                <div style={s.metaField}>
                    <span style={s.metaLabel}>Miles:</span>
                    <span style={s.metaUnderline}>{vehicle?.mileage ?? ""}</span>
                </div>
                <div style={s.metaField}>
                    <span style={s.metaLabel}>Technician:</span>
                    <span style={s.metaUnderline}></span>
                </div>

            </div>

            <div style={s.divider} />

            {/* 3-column checklist */}
            <div style={s.columnsWrapper}>
                {columns.map((col, colIdx) => (
                    <div key={colIdx} style={s.column}>
                        {col.map((row, rowIdx) => {
                            if (row.type === "header") {
                                return (
                                    <div key={`h-${row.cat.id}-${rowIdx}`} style={s.categoryName}>
                                        {row.cat.name}
                                    </div>
                                );
                            }
                            const rec = records[String(row.item.id)];
                            const status = rec?.status;
                            return (
                                <div key={`i-${row.item.id}`} style={s.itemRow}>
                                    <span style={s.itemTitle}>{row.item.title}</span>
                                    <StatusSquares status={status} />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Additional Comments */}
            <div style={s.commentsSection}>
                <div style={s.commentsLabel}>Additional Comments:</div>
                {inspection?.notes ? (
                    <div style={s.commentsText}>{inspection.notes}</div>
                ) : (
                    <div style={s.commentsPlaceholder}>
                        – Use back side of paper for additional notes / repairs needed /
                        damages / recommendations / etc.
                    </div>
                )}
            </div>
        </div>
    );
});

// ── Default export: button + hidden sheet ─────────────────────────────────────

/**
 * Drop-in for the old PrintDVIForm stub.
 *
 * In DviEditor, replace:
 *   <PrintDVIForm />
 * with:
 *   <PrintDVIForm
 *     inspection={inspection}
 *     items={items}
 *     categories={categories}
 *     dealerName="Rishel Auto Sales LLC"   // optional
 *     logoUrl="/logo.png"                   // optional
 *   />
 */
export default function PrintDVIForm({ inspection, items, categories, dealerName, logoUrl }) {
    const contentRef = useRef(null);

    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: `DVI-${inspection?.id ?? "new"}`,
        pageStyle: `
      @page {
        size: letter portrait;
        margin: 12mm 10mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
    });

    return (
        <>
            {/* Visible trigger button */}
            <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handlePrint}
            >
                Print Form
            </button>

            {/* Hidden sheet – off-screen but in the DOM so react-to-print can read it */}
            <div style={{ position: "absolute", left: -9999, top: 0, pointerEvents: "none" }}>
                <PrintDVISheet
                    ref={contentRef}
                    inspection={inspection}
                    items={items}
                    categories={categories}
                    dealerName={dealerName}
                    logoUrl={logoUrl}
                />
            </div>
        </>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
    page: {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: 9,
        color: "#000",
        background: "#fff",
        padding: "6px 8px",
        width: "100%",
        boxSizing: "border-box",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 6,
    },
    headerSpacer: {
        flex: 1,
        visibility: "hidden", // mirrors right side for balance, but invisible
    },
    headerCenter: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
    },
    headerRight: {
        flex: 1,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
    },
    qcarCheck: {
        fontSize: 18,
        color: "#2a7d2e",
        fontWeight: "bold",
        lineHeight: 1,
    },
    qcarTitle: {
        fontSize: 14,
        fontWeight: "bold",
        lineHeight: 1.1,
    },
    qcarSub: {
        fontSize: 7,
        color: "#555",
        fontStyle: "italic",
    },
    sheetTitle: {
        fontSize: 15,
        fontWeight: "bold",
        textDecoration: "underline",
    },
    dealerLogo: {
        height: 60,
        maxWidth: 150,
        objectFit: "contain",
    },
    dealerNameBox: {
        border: "2px solid #000",
        padding: "6px 12px",
        fontWeight: "bold",
        fontSize: 11,
        textAlign: "center",
    },
    metaGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        rowGap: 3,
        columnGap: 20,
        marginBottom: 5,
    },
    metaField: {
        display: "flex",
        alignItems: "baseline",
        gap: 3,
        fontSize: 9,
    },
    metaLabel: {
        fontWeight: "bold",
        whiteSpace: "nowrap",
        fontSize: 12,
    },
    metaUnderline: {
        borderBottom: "1px solid #333",
        flex: 1,
        minWidth: 50,
        fontSize: 12,
        paddingBottom: 1,
    },
    divider: {
        borderTop: "1px solid #000",
        marginBottom: 5,
        marginTop: 10,
    },
    columnsWrapper: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 10,
        alignItems: "start",
    },
    column: {
        display: "flex",
        flexDirection: "column",
    },
    categoryBlock: {
        marginBottom: 5,
    },
    categoryName: {
        fontWeight: "bold",
        textDecoration: "underline",
        fontSize: 14,
        marginBottom: 2,
        marginTop: 5
    },
    itemRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        paddingLeft: 8,
        minHeight: 13,
        lineHeight: 1.35,
    },
    itemTitle: {
        flex: 1,
        fontSize: 12,
        paddingRight: 1,
    },
    commentsSection: {
        marginTop: 8,
        borderTop: "1px solid #aaa",
        paddingTop: 5,
    },
    commentsLabel: {
        fontWeight: "bold",
        textDecoration: "underline",
        fontSize: 12,
        marginBottom: 3,
    },
    commentsText: {
        fontSize: 12,
        whiteSpace: "pre-wrap",
    },
    commentsPlaceholder: {
        fontSize: 9,
        color: "#555",
        fontStyle: "italic",
    },
};